import React from 'react';
import { clearWidgetState } from './services/widgetStateStorage';
/**
 * Top-level catch-all for the IP Assistant widget.
 *
 * If ANY render error escapes (a malformed block, a bad persisted message, an
 * unexpected SSE payload, …), the widget must never just vanish with no way
 * back for a live customer. This boundary catches the error and shows a small
 * recovery card with a "Restart" action that clears the persisted widget state
 * (so a poisoned conversation isn't re-loaded) and remounts the widget fresh.
 */
export class WidgetErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, resetKey: 0 };
        this.handleRestart = this.handleRestart.bind(this);
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[AIChatWidget] crashed and was caught by WidgetErrorBoundary:', error, info);
    }
    handleRestart() {
        // Drop any persisted active conversation so we don't immediately re-render
        // the same crashing thread, then remount the widget subtree fresh.
        clearWidgetState();
        this.setState((prev) => ({ hasError: false, resetKey: prev.resetKey + 1 }));
    }
    render() {
        if (this.state.hasError) {
            // Inline styles keep the recovery card self-contained: it must render
            // correctly even if the crash prevented the widget's stylesheet from
            // mounting. Positioned where the floating widget normally lives.
            return (React.createElement("div", { className: "ai-widget-error-boundary", role: "alert", style: {
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 2147483000,
                    maxWidth: '320px',
                    padding: '16px',
                    borderRadius: '8px',
                    background: '#ffffff',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
                    border: '1px solid #e0e0e0',
                    font: '14px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    color: '#1a1a1a'
                } },
                React.createElement("p", { style: { margin: '0 0 8px', fontWeight: 600 } }, "IP Assistant ran into a problem."),
                React.createElement("p", { style: { margin: '0 0 12px', color: '#555' } }, "Your data is safe. Restarting the assistant will start a fresh conversation."),
                React.createElement("button", { type: "button", onClick: this.handleRestart, style: {
                        cursor: 'pointer',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#2563eb',
                        color: '#ffffff',
                        fontWeight: 600
                    } }, "Restart assistant")));
        }
        // Keying the subtree on resetKey forces a clean remount on recovery.
        return React.createElement(React.Fragment, { key: this.state.resetKey }, this.props.children);
    }
}
